import { useSelector } from 'react-redux';
import React from 'react';
import { RouteContext } from '../../../../context/routeContext';
import { RootState } from '../../../../store/reduxTypes';
import { defaultModules } from '../../../../hooks/usePortalModulesState';
import { ModuleFormValuesType, ModuleTypes } from './moduleManagementTypes';
import { ExtensionViewTypeValue } from '../../ExtensionsPage/ExtensionViewType';

export interface EditModulePageQueryParams {
  moduleId: string;
  moduleType: string;
  preset?: string; // present if this is a preset extension
}
/**
 * This hook is used to get the editing module data from module settings (if it is core module)
 * or from the extensions settings (if it is an extension module).
 */
export const useEditingModule = (): ModuleFormValuesType | null => {
  const { query } = React.useContext(RouteContext);
  const { moduleId, moduleType } = query as EditModulePageQueryParams;
  const moduleSettings = useSelector(
    (state: RootState) => state.settings.moduleSettings,
  );
  const extensionsSettings = useSelector(
    (state: RootState) => state.settings.extensionsSettings,
  );

  const editingModule = React.useMemo(() => {
    if (!moduleId) return null;

    const module = (moduleSettings || []).find(mod => mod.id === moduleId);

    // when it is an extension module, get the extension config and return it
    if (moduleType !== ModuleTypes.Core) {
      const extensionModuleConfig = Object.entries(
        extensionsSettings || {},
      ).find(([extId]) => extId === moduleId);
      if (extensionModuleConfig) {
        const [_, extConfig] = extensionModuleConfig;
        let extensionType = extConfig.type as ModuleTypes;
        if (
          extConfig.type === ModuleTypes.Global &&
          extConfig.isAuthenticated
        ) {
          extensionType = ModuleTypes.Authenticated;
        }

        return {
          ...module,
          type: extensionType,
          label: extConfig.name,
          icon: extConfig.icon,
          embedCode: extConfig.embedCode,
          embedLink: extConfig.embedLink,
          autoSizeEmbed: extConfig.autoSizeEmbed,
          embedlyData: extConfig.embedlyData,
          content: extConfig.embedCode || extConfig.embedLink,
          viewType: extConfig.embedLink
            ? ExtensionViewTypeValue.LINK
            : ExtensionViewTypeValue.EMBED,
        };
      }
    }

    // when core module is not in the moduleSettings store yet
    if (!module) {
      const defaultModule = defaultModules.find(mod => mod.id === moduleId);
      return {
        ...defaultModule,
        label: defaultModule?.title,
        icon: defaultModule?.iconName,
      };
    }

    return module;
  }, [moduleId, moduleSettings, extensionsSettings]);

  return editingModule as ModuleFormValuesType;
};
